import type { DeckIdentity } from "@/interfaces/DeckIdentity";
import { getImgproxyUrl } from "@/lib/imgproxy";
import { gameAssetUrl } from "@/lib/cdn";
import { getCardData } from "@/lib/games/palworld/getCardDataLocal";
import type { PalworldItem } from "@/interfaces/CardItem";

/**
 * Palworld archetype manager.
 *
 * Palworld is an archetype-based game (no leaders). Archetypes are auto-generated
 * from deck composition: {Color1}/{Color2} {Lucky1}/{Lucky2}
 * Example: "Blue/Purple Chillet/Astegon"
 *
 * The archetype data is stored in the tcgfan-backend `archetypes` table and
 * fetched via API. This module provides a client-side interface for frontend
 * components to get archetype information.
 *
 * @see Backend API endpoint: /api/archetypes/:id (to be implemented)
 */

/**
 * Get the image URL for a Palworld archetype.
 * Archetype images are stored in R2 with short unique IDs.
 *
 * @param archetypeId - Short unique archetype ID (e.g., "pw-bp-chillas")
 * @returns Direct CDN URL for the archetype image
 */
function getArchetypeImage(archetypeId: string): string {
  // Use CDN directly without imgproxy for archetype squares
  return gameAssetUrl(
    "palworld",
    "archetype-squares",
    `${archetypeId}.webp`,
    "1.0.5",
  );
}

// Cache for all archetypes by game
const archetypeCache = new Map<number, Map<string, DeckIdentity>>();

/**
 * Clear the in-memory archetype cache for a specific game or all games.
 * Used by the revalidation endpoint to bust the cache.
 *
 * @param gameId - Game ID to clear, or undefined to clear all
 */
export function clearArchetypeCache(gameId?: number): void {
  if (gameId !== undefined) {
    archetypeCache.delete(gameId);
    console.log(`[Archetype Cache] Cleared cache for game ${gameId}`);
  } else {
    archetypeCache.clear();
    console.log(`[Archetype Cache] Cleared all archetype caches`);
  }
}

/**
 * Get archetype from cache synchronously (no API call).
 * Used by client components to check if archetype was pre-fetched.
 *
 * @param gameId - Game ID (e.g., 91 for Palworld)
 * @param archetypeId - Archetype ID to look up
 * @returns DeckIdentity if found in cache, undefined otherwise
 */
export function getArchetypeFromCache(
  gameId: number,
  archetypeId: string,
): DeckIdentity | undefined {
  const gameCache = archetypeCache.get(gameId);
  if (gameCache) {
    return gameCache.get(archetypeId);
  }
  return undefined;
}

/**
 * Fetch all archetypes for a game from the backend API.
 * Results are cached in memory for the lifetime of the server process.
 *
 * @param gameId - Game ID (e.g., 91 for Palworld)
 * @returns Map of archetype ID to DeckIdentity
 */
export async function getAllArchetypesForGame(
  gameId: number,
): Promise<Map<string, DeckIdentity>> {
  // Check cache first
  if (archetypeCache.has(gameId)) {
    return archetypeCache.get(gameId)!;
  }

  try {
    const apiUrl =
      process.env.BACKEND_API_URL ||
      process.env.NEXT_PUBLIC_API_URL ||
      "https://api.tcgfan.com";
    const secretKey = process.env.BACKEND_SECRET_KEY;

    if (!secretKey) {
      console.warn(
        "[Palworld Archetype] BACKEND_SECRET_KEY not configured, returning empty map",
      );
      return new Map();
    }

    const url = `${apiUrl}/api/archetypes?game_id=${gameId}&API_KEY=${secretKey}`;
    console.log(
      "[TEST] Fetching all archetypes from:",
      url.replace(secretKey, "***"),
    );

    const response = await fetch(url, {
      next: {
        revalidate: 3600, // Cache for 1 hour
        tags: [`archetypes-${gameId}`], // Cache tag for invalidation
      },
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();

    // Build map of archetype ID -> DeckIdentity
    const archetypeMap = new Map<string, DeckIdentity>();

    for (const archetype of data) {
      const slug = archetype.name; // API's "name" field is the slug (now primary key)
      const displayName = archetype.display_name || "Unknown Archetype";
      const colorString = Array.isArray(archetype.colors)
        ? archetype.colors.join("/")
        : archetype.color || "Colorless";

      archetypeMap.set(archetype.name, {
        id: archetype.name,
        name: displayName,
        color: colorString,
        set: undefined,
        image: slug
          ? getArchetypeImage(slug)
          : getArchetypeImage("placeholder"),
      });
    }

    // Cache the results
    archetypeCache.set(gameId, archetypeMap);
    console.log(
      "[TEST] Cached",
      archetypeMap.size,
      "archetypes for game",
      gameId,
    );

    return archetypeMap;
  } catch (error) {
    console.error(
      `[Palworld Archetype] Failed to fetch archetypes for game ${gameId}:`,
      error,
    );
    return new Map();
  }
}

/**
 * Fetch archetype data from the backend API.
 *
 * First checks the bulk cache, then falls back to individual fetch if needed.
 * Falls back to deriving from decklist if API is unavailable.
 *
 * @param archetypeId - Archetype ID (stored in decklists.leader_id)
 * @param decklistString - Optional decklist string for fallback derivation
 * @param gameId - Optional game ID for bulk fetching (91 for Palworld)
 * @returns DeckIdentity with archetype info, or undefined if not found
 */
export async function getArchetypeDataFromAPI(
  archetypeId: string,
  decklistString?: string,
  gameId?: number,
): Promise<DeckIdentity | undefined> {
  console.log(
    "[TEST] getArchetypeDataFromAPI called with archetypeId:",
    archetypeId,
    "gameId:",
    gameId,
  );

  try {
    // If gameId provided, try to get from bulk cache first
    if (gameId) {
      const allArchetypes = await getAllArchetypesForGame(gameId);
      const cachedArchetype = allArchetypes.get(archetypeId);
      if (cachedArchetype) {
        console.log(
          "[TEST] Found archetype in bulk cache:",
          cachedArchetype.name,
        );
        return cachedArchetype;
      }
    }

    // Fallback: fetch individual archetype
    const apiUrl =
      process.env.BACKEND_API_URL ||
      process.env.NEXT_PUBLIC_API_URL ||
      "https://api.tcgfan.com";
    const secretKey = process.env.BACKEND_SECRET_KEY;

    if (!secretKey) {
      console.warn(
        "[TEST] BACKEND_SECRET_KEY not configured, falling back to derivation",
      );
      if (decklistString) {
        return deriveArchetypeFromDecklist(decklistString);
      }
      throw new Error("No secret key and no decklist to derive from");
    }

    const url = `${apiUrl}/api/archetypes/${archetypeId}?API_KEY=${secretKey}`;

    const response = await fetch(url, {
      next: {
        revalidate: 3600, // Cache for 1 hour
        tags: [`archetypes-${gameId}`], // Cache tag for invalidation
      },
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();

    // Map API response to DeckIdentity format
    const slug = data.name; // API's "name" field is the slug (now primary key)
    const displayName = data.display_name || "Unknown Archetype";
    const colorString = Array.isArray(data.colors)
      ? data.colors.join("/")
      : data.color || "Colorless";

    console.log(
      "[TEST] Mapped values - slug:",
      slug,
      "displayName:",
      displayName,
      "color:",
      colorString,
    );

    const result = {
      id: data.name || archetypeId,
      name: displayName,
      color: colorString,
      set: undefined,
      image: slug ? getArchetypeImage(slug) : getArchetypeImage("placeholder"),
    };

    return result;
  } catch (error) {
    console.warn(
      `[Palworld Archetype] API fetch failed for ${archetypeId}, falling back to derivation:`,
      error,
    );

    // Fallback: If API is unavailable and we have a decklist, derive the archetype
    if (decklistString) {
      return deriveArchetypeFromDecklist(decklistString);
    }

    // Final fallback: return placeholder
    return {
      id: archetypeId,
      name: "Unknown Archetype",
      color: "Colorless",
      set: undefined,
      image: getArchetypeImage("placeholder"),
    };
  }
}

/**
 * Get archetype data for a Palworld deck.
 *
 * This is the main entry point used by the game handler's getLeaderData().
 * For Palworld, the "leader" is actually an archetype derived from the decklist.
 *
 * NOTE: Since Palworld archetypes are derived from decklist contents,
 * this function returns a placeholder. Components should use
 * deriveArchetypeFromDecklist() directly with the full decklist string.
 *
 * @param archetypeId - Archetype ID from decklists.leader_id (typically "1" for Palworld)
 * @returns DeckIdentity with placeholder archetype info
 */
export function getArchetypeData(
  archetypeId: string,
): DeckIdentity | undefined {
  // Palworld archetypes are derived from decklist contents, not stored IDs
  // Return a placeholder that components can override by calling deriveArchetypeFromDecklist()
  return {
    id: archetypeId,
    name: "Archetype",
    color: "Colorless",
    set: undefined,
    image: getArchetypeImage("placeholder"),
  };
}

/**
 * Helper to parse colors from archetype name.
 * Example: "Blue/Purple Chillet/Astegon" -> ["Blue", "Purple"]
 *
 * @param archetypeName - Full archetype name
 * @returns Array of colors
 */
export function parseArchetypeColors(archetypeName: string): string[] {
  const colorPart = archetypeName.split(" ")[0]; // "Blue/Purple"
  return colorPart.split("/").map((c) => c.trim());
}

/**
 * Helper to parse lucky pals from archetype name.
 * Example: "Blue/Purple Chillet/Astegon" -> ["Chillet", "Astegon"]
 *
 * @param archetypeName - Full archetype name
 * @returns Array of lucky pal names
 */
export function parseArchetypeLuckyPals(archetypeName: string): string[] {
  const parts = archetypeName.split(" ");
  if (parts.length < 2) return [];

  const luckyPart = parts.slice(1).join(" "); // "Chillet/Astegon"
  return luckyPart.split("/").map((p) => p.trim());
}

/**
 * Analyze a decklist to determine the archetype name and colors.
 * Used as fallback when leader_id/leader_name is unknown.
 *
 * @param decklistString - Decklist string (e.g., "4xEBP01-001;2xEBP01-002;...")
 * @returns DeckIdentity with derived archetype info
 */
export function deriveArchetypeFromDecklist(
  decklistString: string,
): DeckIdentity {
  // Parse the decklist to find the most played lucky pals and colors
  const cardCounts: { [cardId: string]: number } = {};
  const luckyPalData: {
    [palName: string]: { count: number; cost: number; colors: string[] };
  } = {};
  const colorSet = new Set<string>();

  const cards = decklistString.split(";").filter((c) => c.trim());

  for (const card of cards) {
    const [countStr, cardId] = card.split("x");
    if (!countStr || !cardId) continue;

    const count = parseInt(countStr);
    if (isNaN(count)) continue;

    cardCounts[cardId] = count;

    // Get card data to determine if it's a lucky pal and its color
    const cardData = getCardData(cardId);
    if (!cardData) continue;

    // Track colors from all cards
    if (cardData.color && cardData.color !== "Colorless") {
      // Handle multi-color cards (e.g., "Blue/Purple")
      const colors = cardData.color.split("/").map((c) => c.trim());
      colors.forEach((color) => colorSet.add(color));
    }

    // Track lucky pals - they have the "lucky" field set to "☆"
    if (cardData.lucky === "☆") {
      // Extract main name (before "–" or "—" if present)
      const mainName = cardData.name
        ? cardData.name.split(/[–—]/)[0].trim()
        : cardId;

      const palColors = cardData.color
        ? cardData.color.split("/").map((c) => c.trim())
        : [];

      if (!luckyPalData[mainName]) {
        luckyPalData[mainName] = {
          count: count,
          cost: cardData.cost ?? 0,
          colors: palColors,
        };
      } else {
        luckyPalData[mainName].count += count;
      }
    }
  }

  // Get top 2 lucky pals by count, then sort by cost (cheap to expensive)
  const topLuckyPalsData = Object.entries(luckyPalData)
    .sort(([, a], [, b]) => b.count - a.count) // First sort by count (most played first)
    .slice(0, 2) // Take top 2 by count
    .sort(([, a], [, b]) => a.cost - b.cost); // Then sort by cost (cheap to expensive)

  const topLuckyPals = topLuckyPalsData.map(([name]) => name);

  // Define color order: Red, Blue, Green, Purple
  const colorOrder = ["Red", "Blue", "Green", "Purple"];

  // Use colors from ALL cards in the deck (not just Lucky Pals)
  // This ensures archetypes like "Digitoise & Lyleen" show as "Blue/Green"
  // even if the Lucky Pals themselves aren't both colors
  const colors = Array.from(colorSet)
    .sort((a, b) => {
      const indexA = colorOrder.indexOf(a);
      const indexB = colorOrder.indexOf(b);
      // If color not in order array, put it at the end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    })
    .slice(0, 2);

  // Build archetype name
  const colorPart = colors.length > 0 ? colors.join("/") : "Colorless";
  const luckyPart =
    topLuckyPals.length > 0 ? topLuckyPals.join(" & ") : "Unknown";

  const archetypeName = `${luckyPart}`;
  const archetypeColor = colorPart;

  // For derived archetypes (not from API), we don't have a slug mapping
  // Use placeholder image - proper images only available via API
  return {
    id: "derived",
    name: archetypeName,
    color: archetypeColor,
    set: undefined,
    image: getArchetypeImage("placeholder"),
  };
}
